import React, { useEffect, useState } from "react";
import { Shuffle, Check, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

/**
 * RegenerateLookPanel — one-click "give me 3 candidate looks" for a site.
 *
 * The operator hits "Shuffle 3 new looks" → we call
 * `GET /api/sites/{id}/looks/candidates?n=3` → render 3 preview cards with a
 * palette swatch + LIVE iframe preview of the actual site + font sample +
 * header/footer/layout labels → operator clicks one → we call
 * `POST /api/sites/{id}/looks/apply` → parent reloads.
 *
 * The iframe uses the same `?preview_*=` SSR overrides that the ThemePickerPanel
 * uses (admin-only, auth-scoped), so the preview is a faithful render of the
 * ACTUAL articles under the proposed combo.
 */
const PREVIEW_W = 480;
const PREVIEW_H = 300;
const PREVIEW_SCALE = 0.375; // 1280 render → 480 tile

export default function RegenerateLookPanel({ site, reload }) {
  const [candidates, setCandidates] = useState([]);
  const [paletteMeta, setPaletteMeta] = useState({});
  const [fontMeta, setFontMeta] = useState({});
  const [busy, setBusy] = useState(false);
  const [applyingIdx, setApplyingIdx] = useState(null);
  const [flash, setFlash] = useState(null);
  const [bust, setBust] = useState(0); // iframe cache-buster

  const current = {
    header_variant: site.header_variant,
    footer_variant: site.footer_variant,
    template: site.template,
    variant: site.variant,
    palette: site.palette,
    font_pair: site.font_pair,
  };

  async function shuffle() {
    setBusy(true);
    setFlash(null);
    try {
      const res = await api.get(`/sites/${site.id}/looks/candidates?n=3`);
      setCandidates(res.data.candidates || []);
      setPaletteMeta(res.data.palette_meta || {});
      setFontMeta(res.data.font_meta || {});
      setBust((b) => b + 1);
    } catch (e) {
      const msg = (e?.response?.data?.detail) || "Failed to fetch candidates";
      setFlash({ kind: "err", msg: String(msg) });
    } finally {
      setBusy(false);
    }
  }

  async function apply(look, idx) {
    setApplyingIdx(idx);
    setFlash(null);
    try {
      await api.post(`/sites/${site.id}/looks/apply`, look);
      await reload();
      const p = paletteMeta[look.palette]?.label || look.palette;
      const f = fontMeta[look.font_pair]?.label || look.font_pair;
      setFlash({
        kind: "ok",
        msg: `Applied: ${look.header_variant} · ${look.template}/${look.variant} · ${p} · ${f}`,
      });
      setCandidates([]);
    } catch (e) {
      const msg = (e?.response?.data?.detail) || "Failed to apply look";
      setFlash({ kind: "err", msg: String(msg) });
    } finally {
      setApplyingIdx(null);
    }
  }

  function previewUrl(look) {
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/render/sites/${site.slug}`;
    const p = new URLSearchParams();
    p.set("preview_header", look.header_variant);
    p.set("preview_footer", look.footer_variant);
    p.set("preview_template", look.template);
    p.set("preview_variant", look.variant);
    p.set("preview_palette", look.palette);
    p.set("preview_font", look.font_pair);
    p.set("_b", bust);
    return `${base}?${p.toString()}`;
  }

  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-900/50 p-4" data-testid="regenerate-look-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-semibold tracking-wide uppercase">Auto-generate look</h3>
          </div>
          <p className="text-xs text-zinc-400 mt-1 max-w-lg">
            One click → 3 palette-safe candidate combos with live SSR previews. Pick the one that speaks to you.
            All combos are pre-validated for color contrast + responsive rendering.
          </p>
        </div>
        <button
          onClick={shuffle}
          disabled={busy}
          data-testid="regenerate-look-shuffle-btn"
          className="inline-flex items-center gap-2 rounded-sm border border-emerald-700 bg-emerald-950/40 hover:bg-emerald-900/40 px-4 py-2 text-sm font-semibold disabled:opacity-50 transition"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shuffle className="w-4 h-4" />}
          {candidates.length ? "Shuffle again" : "Shuffle 3 new looks"}
        </button>
      </div>

      {/* Flash */}
      {flash && (
        <div
          data-testid={flash.kind === "ok" ? "regenerate-look-success" : "regenerate-look-error"}
          className={`mt-3 rounded-sm border px-3 py-2 text-xs ${flash.kind === "ok" ? "border-emerald-800 bg-emerald-950/40 text-emerald-300" : "border-red-800 bg-red-950/40 text-red-300"}`}
        >
          {flash.msg}
        </div>
      )}

      {/* Current look micro-summary */}
      <div className="mt-3 text-xs text-zinc-500" data-testid="regenerate-look-current">
        <span className="opacity-60">Current:</span>{" "}
        <span className="font-mono">
          {current.header_variant} · {current.template}/{current.variant} · {current.palette} · {current.font_pair}
        </span>
      </div>

      {/* Candidate cards — expanded to fit iframe */}
      {candidates.length > 0 && (
        <div
          className="mt-4 grid gap-4"
          style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${PREVIEW_W + 16}px, 1fr))` }}
          data-testid="regenerate-look-candidates"
        >
          {candidates.map((look, idx) => (
            <CandidateCard
              key={`${look.template}-${look.variant}-${look.header_variant}-${look.footer_variant}-${look.palette}-${look.font_pair}`}
              idx={idx}
              look={look}
              paletteMeta={paletteMeta}
              fontMeta={fontMeta}
              busy={applyingIdx === idx}
              onApply={() => apply(look, idx)}
              previewUrl={previewUrl(look)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CandidateCard({ idx, look, paletteMeta, fontMeta, busy, onApply, previewUrl }) {
  const pmeta = paletteMeta[look.palette] || {};
  const fmeta = fontMeta[look.font_pair] || {};
  const bg = pmeta.bg || "#ffffff";
  const text = pmeta.text || "#111";
  const accent = pmeta.accent || "#c00";
  return (
    <div
      data-testid={`regenerate-look-candidate-${idx}`}
      className="group relative rounded-md border border-zinc-800 hover:border-emerald-700 transition overflow-hidden bg-zinc-900 flex flex-col"
    >
      {/* Palette stripe (thin colour chip on top) */}
      <div className="h-2 flex" title={pmeta.label || look.palette}>
        <div style={{ background: bg, flex: 2 }} />
        <div style={{ background: accent, flex: 1 }} />
        <div style={{ background: text, flex: 1 }} />
      </div>

      {/* Live SSR iframe preview — the marquee feature */}
      <div
        className="relative border-b border-zinc-800"
        style={{ width: "100%", height: PREVIEW_H, overflow: "hidden", background: bg }}
      >
        <iframe
          src={previewUrl}
          title={`Preview ${look.header_variant} · ${look.palette}`}
          loading="lazy"
          style={{
            width: PREVIEW_W / PREVIEW_SCALE,
            height: PREVIEW_H / PREVIEW_SCALE,
            border: 0,
            transform: `scale(${PREVIEW_SCALE})`,
            transformOrigin: "top left",
            pointerEvents: "none",
          }}
        />
      </div>

      {/* Metadata — compact 2-col grid */}
      <div className="p-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs flex-1">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Palette</div>
          <div className="text-zinc-200 truncate">{pmeta.label || look.palette}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Font</div>
          <div className="text-zinc-200 truncate">{fmeta.label || look.font_pair}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Layout</div>
          <div className="text-zinc-200 truncate">{look.template} · {look.variant}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Header</div>
          <div className="text-zinc-200 truncate">{look.header_variant}</div>
        </div>
        <div className="col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Footer</div>
          <div className="text-zinc-200 truncate">{look.footer_variant}</div>
        </div>
      </div>

      {/* Apply button */}
      <button
        onClick={onApply}
        disabled={busy}
        data-testid={`regenerate-look-apply-${idx}`}
        className="w-full flex items-center justify-center gap-1.5 py-2 border-t border-zinc-800 text-xs font-semibold uppercase tracking-wider hover:bg-emerald-900/30 disabled:opacity-50 transition"
      >
        {busy ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Applying…
          </>
        ) : (
          <>
            <Check className="w-3.5 h-3.5" /> Apply this look
          </>
        )}
      </button>
    </div>
  );
}
